import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import { newsroomItems, FILTER_OPTIONS } from './newsroomData';
import './Newsroom.css';

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const NewsroomCard = ({ item }) => (
  <article className="newsroom-card">
    <Link to={`/newsroom/${item.slug}`} className="newsroom-card__link" aria-label={`Read: ${item.title}`}>
      {item.coverType === 'nvidia' && (
        <div className="newsroom-card__cover newsroom-card__cover--nvidia">
          <img
            src={item.image}
            alt={item.imageAlt}
            className="newsroom-card__badge-img"
          />
        </div>
      )}

      {item.coverType === 'intechsante' && item.eventVisual && (
        <div className="newsroom-card__cover newsroom-card__cover--intechsante">
          <div className="newsroom-intechsante-cover">
            <img
              src={item.image}
              alt={item.imageAlt}
              className="newsroom-card__intechsante-logo"
            />
            <div className="event-visual">
              <span className="event-visual__location">{item.eventVisual.location}</span>
              <div className="event-visual__date">
                <span className="event-visual__date-day">{item.eventVisual.day}</span>
                <span className="event-visual__date-mon">{item.eventVisual.month}</span>
                <span className="event-visual__date-year">{item.eventVisual.year}</span>
              </div>
              <span className="event-visual__name">{item.eventVisual.name}</span>
            </div>
          </div>
        </div>
      )}

      {item.coverType === 'siliconindia' && (
        <div className="newsroom-card__cover newsroom-card__cover--siliconindia">
          <img
            src={item.image}
            alt={item.imageAlt}
            className="newsroom-card__certificate-img"
          />
        </div>
      )}

      <div className="newsroom-card__body">
        <div className="newsroom-card__meta">
          <span className={`newsroom-card__tag newsroom-card__tag--${item.tagVariant}`}>
            {item.tagLabel}
          </span>
          <span className="newsroom-card__date">{item.date}</span>
        </div>

        <h3 className="newsroom-card__title">
          {item.slug === 'nvidia-inception' ? (
            <>
              Vardaan joins the <span className="program-name">NVIDIA Inception</span> Program.
            </>
          ) : (
            item.title
          )}
        </h3>

        <p className="newsroom-card__excerpt">{item.excerpt}</p>

        <span className="newsroom-card__cta">
          Read the announcement
          <ArrowIcon />
        </span>
      </div>
    </Link>
  </article>
);

const Newsroom = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredItems =
    activeFilter === 'all'
      ? newsroomItems
      : newsroomItems.filter((item) => item.filterKeys?.includes(activeFilter));

  const storyCount = String(newsroomItems.length).padStart(2, '0');

  return (
    <div className="newsroom-page">
      <header className="newsroom-hero">
        <div className="newsroom-container newsroom-hero-grid">
          <div>
            <div className="newsroom-eyebrow">
              <span className="newsroom-eyebrow-dot" />
              Vardaan
              <span className="newsroom-eyebrow-pipe">·</span>
              Newsroom
              <span className="newsroom-eyebrow-pipe">·</span>
              2026
            </div>

            <h1 className="newsroom-hero-title">
              What we&apos;re <em>doing,</em> where it&apos;s heading.
            </h1>

            <p className="newsroom-hero-lede">
              A running record of Vardaan&apos;s work — the products we&apos;re shipping, the programs and communities we&apos;re joining, and the conversations we&apos;re having about the future of enterprise AI.
            </p>
          </div>

          <aside className="newsroom-hero-side" aria-label="Story count">
            <div className="newsroom-hero-count">
              <span className="newsroom-hero-count-number">{storyCount}</span>
              Stories · 2026
            </div>
          </aside>
        </div>
      </header>

      <div className="newsroom-filter-strip">
        <div className="newsroom-container newsroom-filter-inner">
          <div className="newsroom-filter-label">Filter</div>
          <div className="newsroom-filter-pills" role="tablist" aria-label="Filter newsroom stories">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`newsroom-filter-pill${activeFilter === option.id ? ' active' : ''}`}
                role="tab"
                aria-selected={activeFilter === option.id}
                onClick={() => setActiveFilter(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="newsroom-news-section">
        <div className="newsroom-container">
          <div className="newsroom-news-grid">
            {filteredItems.map((item) => (
              <NewsroomCard key={item.slug} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="newsroom-stay-tuned">
        <div className="newsroom-container">
          <div className="newsroom-stay-tuned-inner">
            <div className="newsroom-stay-tuned-eyebrow">Stay close to the work</div>

            <h2>
              Watch a category get built — <em>from the inside.</em>
            </h2>

            <p className="newsroom-stay-tuned-lede">
              AI is rewriting how the world&apos;s most regulated industries operate — and the trust infrastructure underneath them. Vardaan sits at the centre of that shift, building the platforms and AI architecture that let governance, risk, and compliance keep pace with the speed today demands. The newsroom is where you&apos;ll see all of it unfold — in real time, written first-hand, with a clear view of where this category is heading next.
            </p>

            <div className="newsroom-stay-tuned-items" role="list" aria-label="What you'll receive">
              <div className="newsroom-stay-item" role="listitem">
                <div className="newsroom-stay-item-num">01</div>
                <div>
                  <h4>Product announcements</h4>
                  <p>When we ship new capabilities across RiskaVaire and the wider portfolio — what changed, why it matters, and how to put it to work.</p>
                </div>
              </div>
              <div className="newsroom-stay-item" role="listitem">
                <div className="newsroom-stay-item-num">02</div>
                <div>
                  <h4>Blog &amp; field notes</h4>
                  <p>What we&apos;re learning building production-grade AI for compliance, risk, and regulated decision-making — engineering, design, and the calls we got right and wrong.</p>
                </div>
              </div>
              <div className="newsroom-stay-item" role="listitem">
                <div className="newsroom-stay-item-num">03</div>
                <div>
                  <h4>Speaking &amp; press</h4>
                  <p>Where we&apos;ll be live next, what we&apos;re saying when we get there, and the conversations the broader category is having about AI, governance, and trust.</p>
                </div>
              </div>
              <div className="newsroom-stay-item" role="listitem">
                <div className="newsroom-stay-item-num">04</div>
                <div>
                  <h4>Forward views</h4>
                  <p>Our take on where the trust and intelligence layer of the modern enterprise is heading — and what that means for the teams operating, governing, and building in regulated industries.</p>
                </div>
              </div>
            </div>

            <div className="newsroom-stay-tuned-actions">
              <Link to="/blog" className="newsroom-btn newsroom-btn-outline-light">
                Read the blog
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Newsroom;
